import { DEFAULT_LANGUAGE } from '@/constants';
import type { IPmtOnboardingPopup } from '@/interfaces/tracking';
import { getMasterTranslationLanguageCode, languageFieldsMapping, translationDataModify } from '@/utils';
import callContentful from './base.fetchers';

import { LanguageCode } from '@/interfaces';

type GetPmtOnboardingPopUps = (args: {
  isPreviewMode: boolean;
  languageCode?: LanguageCode;
}) => Promise<{
  pmtOnboardingPopup: IPmtOnboardingPopup[];
  pmtEmailSyncOnboardingPopup: IPmtOnboardingPopup[];
}>;

const getPmtOnboardingPopUp: GetPmtOnboardingPopUps = async ({
  isPreviewMode,
  languageCode = LanguageCode.English,
}) => {
  const mappedLanguageFields = languageFieldsMapping(languageCode);
  const languageField = getMasterTranslationLanguageCode(languageCode);

  const query = `
    {
      defaultPmtOnboardingPopup: pmtOnboardingPopupCollection(
        preview: ${isPreviewMode}
        locale: "${DEFAULT_LANGUAGE}"
        limit: 1
        where: {
          isEmailSyncPopup_not: true
        }
      ) {
        items {
          sys {
            id
          }
          identifier
          joinUsHeader {
            en
          }
          joinUsValuePropositionList
          joinUsDesktopMainText {
            en
          }
          joinUsSubtext {
            en
          }
          subPmcNewsletterText {
            en
          }
          newsletterSubText {
            en
          }
          heroImage {
            contentType
            url
            description
          }
          zapierHookId
          newUserTriggerTimes
          returnUserTriggerTimes
          trigger
          variant
          loginByEmail
        }
      }
      pmtOnboardingPopup: pmtOnboardingPopupCollection(
        preview: ${isPreviewMode}
        locale: "${mappedLanguageFields.locale}"
        limit: 10
        where: {
          isEmailSyncPopup_not: true
        }
      ) {
        items {
          sys {
            id
          }
          identifier
          joinUsHeader {
            ${languageField}
          }
          joinUsDesktopMainText {
            ${languageField}
          }
          joinUsSubtext {
            ${languageField}
          }
          subPmcNewsletterText {
            ${languageField}
          }
          newsletterSubText {
            ${languageField}
          }
          joinUsValuePropositionList
          heroImage {
            contentType
            url
            description
          }
          zapierHookId
          newUserTriggerTimes
          returnUserTriggerTimes
          trigger
          variant
          loginByEmail
        }
      }
      defaultPmtEmailSyncOnboardingPopup: pmtOnboardingPopupCollection(
        preview: ${isPreviewMode}
        locale: "${DEFAULT_LANGUAGE}"
        limit: 1
        where: {
          isEmailSyncPopup: true
        }
      ) {
        items {
          sys {
            id
          }
          identifier
          joinUsHeader {
            en
          }
          joinUsValuePropositionList
          joinUsDesktopMainText {
            en
          }
          joinUsSubtext {
            en
          }
          subPmcNewsletterText {
            en
          }
          newsletterSubText {
            en
          }
          heroImage {
            contentType
            url
            description
          }
          desktopGoogleCta {
            en
          }
          zapierHookId
          newUserTriggerTimes
          returnUserTriggerTimes
          trigger
          variant
          loginByEmail
        }
      }
      pmtEmailSyncOnboardingPopup: pmtOnboardingPopupCollection(
        preview: ${isPreviewMode}
        locale: "${mappedLanguageFields.locale}"
        limit: 10
        where: {
          isEmailSyncPopup: true
        }
      ) {
        items {
          sys {
            id
          }
          identifier
          joinUsHeader {
            ${languageField}
          }
          joinUsDesktopMainText {
            ${languageField}
          }
          joinUsSubtext {
            ${languageField}
          }
          desktopGoogleCta {
            ${languageField}
          }
          subPmcNewsletterText {
            ${languageField}
          }
          newsletterSubText {
            ${languageField}
          }
          joinUsValuePropositionList
          heroImage {
            contentType
            url
            description
          }
          zapierHookId
          newUserTriggerTimes
          returnUserTriggerTimes
          trigger
          variant
          loginByEmail
        }
      }
    }
  `;

  const slug = `/${languageCode === LanguageCode.English ? '' : '/'}track-my-parcel`;
  const response = await callContentful(query, isPreviewMode, slug, 'pmtPopup.fetchers.getPmtOnboardingPopUp');
  const rawDefaultPmtPopupData = response?.data?.defaultPmtOnboardingPopup?.items?.filter(Boolean)?.[0] || null;
  const rawPmtPopupData: IPmtOnboardingPopup[] = response.data?.pmtOnboardingPopup?.items;

  const rawDefaultPmtEmailSyncPopupData =
    response?.data?.defaultPmtEmailSyncOnboardingPopup?.items?.filter(Boolean)?.[0] || null;

  const rawPmtEmailSyncPopupData: IPmtOnboardingPopup[] = response.data?.pmtEmailSyncOnboardingPopup?.items;

  // Transform multiLanguageField data
  const transformPopupData = (item: Record<string, unknown>) => {
    const joinUsHeader = item.joinUsHeader as Record<string, string> | null;
    const joinUsDesktopMainText = item.joinUsDesktopMainText as Record<string, string> | null;
    const joinUsSubtext = item.joinUsSubtext as Record<string, string> | null;
    const subPmcNewsletterText = item.subPmcNewsletterText as Record<string, string> | null;
    const newsletterSubText = item.newsletterSubText as Record<string, string> | null;
    const desktopGoogleCta = item.desktopGoogleCta as Record<string, string> | null;

    return {
      ...item,
      joinUsHeader: joinUsHeader?.[languageField] || '',
      joinUsDesktopMainText: joinUsDesktopMainText?.[languageField] || '',
      joinUsSubtext: joinUsSubtext?.[languageField] || '',
      subPmcNewsletterText: subPmcNewsletterText?.[languageField] || '',
      newsletterSubText: newsletterSubText?.[languageField] || '',
      ...(desktopGoogleCta && {
        desktopGoogleCta: desktopGoogleCta?.[languageField] || '',
      }),
    };
  };

  const transformDefaultData = (item: Record<string, unknown>) => {
    const joinUsHeader = item.joinUsHeader as Record<string, string> | null;
    const joinUsDesktopMainText = item.joinUsDesktopMainText as Record<string, string> | null;
    const joinUsSubtext = item.joinUsSubtext as Record<string, string> | null;
    const subPmcNewsletterText = item.subPmcNewsletterText as Record<string, string> | null;
    const newsletterSubText = item.newsletterSubText as Record<string, string> | null;
    const desktopGoogleCta = item.desktopGoogleCta as Record<string, string> | null;

    return {
      ...item,
      joinUsHeader: joinUsHeader?.en || '',
      joinUsDesktopMainText: joinUsDesktopMainText?.en || '',
      joinUsSubtext: joinUsSubtext?.en || '',
      subPmcNewsletterText: subPmcNewsletterText?.en || '',
      newsletterSubText: newsletterSubText?.en || '',
      ...(desktopGoogleCta && {
        desktopGoogleCta: desktopGoogleCta?.en || '',
      }),
    };
  };

  const transformedDefaultPmtPopupData = rawDefaultPmtPopupData
    ? transformDefaultData(rawDefaultPmtPopupData as Record<string, unknown>)
    : {};
  const transformedPmtPopupData = rawPmtPopupData?.map((item) => transformPopupData(item as Record<string, unknown>));

  const transformedDefaultPmtEmailSyncPopupData = rawDefaultPmtEmailSyncPopupData
    ? transformDefaultData(rawDefaultPmtEmailSyncPopupData as Record<string, unknown>)
    : {};
  const transformedPmtEmailSyncPopupData = rawPmtEmailSyncPopupData?.map((item) =>
    transformPopupData(item as Record<string, unknown>),
  );

  return {
    pmtOnboardingPopUp: transformedPmtPopupData?.map((pmtOnboardingPopup) =>
      translationDataModify(transformedDefaultPmtPopupData, pmtOnboardingPopup),
    ),
    pmtEmailSyncOnboardingPopUp: transformedPmtEmailSyncPopupData?.map((emailSyncOnboardingPopup) =>
      translationDataModify(transformedDefaultPmtEmailSyncPopupData, emailSyncOnboardingPopup),
    ),
  } as any;
};

export { getPmtOnboardingPopUp };
